/**
 * Team Inbox — unified SEND PICKER (TEAMINBOX_SEND_PICKER_BUILD_SPEC).
 *
 * One "Send a message" surface replacing the old flat template list. Two tabs
 * (⚡ Canned / 📋 Templates), sticky header + footer with the list/preview
 * scrolling between them, a searchable category-grouped list, and a master–
 * detail WhatsApp-bubble preview of the ACTUAL message content. Fill-in-the-
 * blanks with a required-gate (the #132000 fix, client side). Name auto-fill
 * via the 3-level hierarchy (saved contact → WhatsApp profile → graceful
 * generic). Canned locks 🔒 outside the 24h window (auto-switch to Templates).
 * Mobile = single column with tap→preview + "‹ Back". Pure logic = lib/sendPicker.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ApprovedTemplate } from '../../../lib/messagesStore';
import type { CannedReply } from '../lib/types';
import { templateMeta } from '../lib/templates';

/**
 * Body + variable labels for one template.
 *
 * The local registry wins when it knows the template (it carries friendly
 * blank labels like "First name"); otherwise we fall back to Meta's own body,
 * which the server now ships for EVERY approved template. Before this, a
 * template missing from the local registry previewed as blank — which is why
 * only the handful of registered ones felt usable.
 */
function metaFor(t: { name: string; body?: string; paramCount?: number | null }) {
  const local = templateMeta(t.name);
  if (local.body) return local;
  const body = t.body || '';
  const n =
    typeof t.paramCount === 'number'
      ? t.paramCount
      : (body.match(/\{\{(\d+)\}\}/g) || []).length;
  return { body, variables: Array.from({ length: n }, (_, i) => `Blank ${i + 1}`) };
}
import {
  buildTemplateParams,
  cannedEn,
  cannedHi,
  cannedId,
  cannedMissing,
  cannedReady,
  cannedTitle,
  cannedVars,
  groupCannedByCategory,
  humanize,
  isNameLabel,
  isNameTok,
  nameHint,
  previewCannedSegments,
  previewTplSegments,
  resolveName,
  segmentsToText,
  tplMissing,
  tplReady,
  type NameContext,
  type PreviewSegment,
} from '../lib/sendPicker';

type Tab = 'canned' | 'tpl';

export interface SendPickerProps {
  /** Recipient display ("Rohit Khandelwal" or a WA-profile name or the number). */
  recipientLabel: string;
  recipientPhone: string | null;
  /** Is the 24h window open? Drives canned-lock + the window banner. */
  windowOpen: boolean;
  /** Visible canned messages (already filtered to shared + my personal). */
  canned: CannedReply[];
  /** Meta-approved templates (server source of truth). */
  templates: ApprovedTemplate[];
  /** Name hierarchy inputs (§5): saved-contact first name + WA profile name. */
  nameCtx: NameContext;
  sending: boolean;
  /** Send a CANNED message (resolved plain text). Resolves true on success. */
  onSendCanned: (text: string) => Promise<boolean>;
  /** Send a TEMPLATE (name + ordered params). Resolves true on success. */
  onSendTemplate: (templateName: string, params: string[]) => Promise<boolean>;
  onClose: () => void;
  /** Force the single-column mobile layout (tests/harness); else auto by width. */
  forceMobile?: boolean;
}

/** Render preview segments into spans for the WhatsApp bubble. */
function Bubble({ segs }: { segs: PreviewSegment[] }) {
  return (
    <div className="ti-sp-wa-wrap">
      <div className="ti-sp-wa-bubble">
        {segs.map((s, i) =>
          s.kind === 'text' ? (
            <span key={i}>{s.text}</span>
          ) : s.kind === 'filled' ? (
            <span key={i} className="filled">
              {s.text}
            </span>
          ) : (
            <span key={i} className="ph">
              {s.text}
            </span>
          ),
        )}
        <div className="ti-sp-wa-time">now ✓</div>
      </div>
    </div>
  );
}

export function SendPicker({
  recipientLabel,
  recipientPhone,
  windowOpen,
  canned,
  templates,
  nameCtx,
  sending,
  onSendCanned,
  onSendTemplate,
  onClose,
  forceMobile,
}: SendPickerProps) {
  const [tab, setTab] = useState<Tab>(windowOpen ? 'canned' : 'tpl');
  const [search, setSearch] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [lang, setLang] = useState<'en' | 'hi'>('en');
  const [showPrev, setShowPrev] = useState(false); // mobile detail open

  // Outside the window the canned tab is locked → force Templates.
  useEffect(() => {
    if (!windowOpen && tab === 'canned') setTab('tpl');
  }, [windowOpen, tab]);

  const { source, name } = useMemo(() => resolveName(nameCtx), [nameCtx]);

  const cannedGroups = useMemo(() => groupCannedByCategory(canned), [canned]);

  // ── selected item resolution ──
  const selCanned = useMemo(
    () => (tab === 'canned' ? canned.find((c) => cannedId(c) === selId) || null : null),
    [tab, canned, selId],
  );
  const selTpl = useMemo(
    () => (tab === 'tpl' ? templates.find((t) => t.name === selId) || null : null),
    [tab, templates, selId],
  );

  const selCannedVars = useMemo(
    () => (selCanned ? cannedVars(cannedEn(selCanned), cannedHi(selCanned)) : []),
    [selCanned],
  );
  const selTplMeta = useMemo(
    () => (selTpl ? metaFor(selTpl) : null),
    [selTpl],
  );

  // Pick an item → auto-fill name slots from the resolved name.
  function pick(id: string) {
    setSelId(id);
    setLang('en');
    const v: Record<string, string> = {};
    if (name) {
      if (tab === 'canned') {
        const c = canned.find((x) => cannedId(x) === id);
        if (c) cannedVars(cannedEn(c), cannedHi(c)).forEach((t) => { if (isNameTok(t)) v[t] = name; });
      } else {
        const t = templates.find((x) => x.name === id);
        const meta = t ? metaFor(t) : null;
        (meta?.variables || []).forEach((lab, i) => { if (isNameLabel(lab)) v[`v${i + 1}`] = name; });
      }
    }
    setValues(v);
    setShowPrev(true);
  }

  function switchTab(next: Tab) {
    if (next === 'canned' && !windowOpen) return; // locked
    if (next === tab) {
      // tapping the active tab on mobile returns to the list (spec §3/§8)
      setShowPrev(false);
      return;
    }
    setTab(next);
    setSelId(null);
    setValues({});
    setLang('en');
    setSearch('');
    setShowPrev(false);
  }

  function setVal(key: string, val: string) {
    setValues((prev) => ({ ...prev, [key]: val }));
  }

  // ── preview segments (live) ──
  const previewSegs: PreviewSegment[] = useMemo(() => {
    if (selCanned) {
      const body = lang === 'hi' ? cannedHi(selCanned) || cannedEn(selCanned) : cannedEn(selCanned);
      return previewCannedSegments(body, values, name);
    }
    if (selTpl && selTplMeta) {
      return previewTplSegments(selTplMeta.body, selTplMeta.variables, values, name);
    }
    return [];
  }, [selCanned, selTpl, selTplMeta, values, name, lang]);

  // ── ready / missing gate ──
  const ready = useMemo(() => {
    if (selCanned) return cannedReady(selCannedVars, values);
    if (selTpl && selTplMeta) return tplReady(selTplMeta.variables, values);
    return false;
  }, [selCanned, selTpl, selTplMeta, selCannedVars, values]);
  const missing = useMemo(() => {
    if (selCanned) return cannedMissing(selCannedVars, values);
    if (selTpl && selTplMeta) return tplMissing(selTplMeta.variables, values);
    return 0;
  }, [selCanned, selTpl, selTplMeta, selCannedVars, values]);

  const cannedLocked = !windowOpen;

  async function doSend() {
    if (!ready || sending) return;
    if (selCanned) {
      const text = segmentsToText(previewSegs);
      const ok = await onSendCanned(text);
      if (ok) onClose();
    } else if (selTpl && selTplMeta) {
      const params = buildTemplateParams(selTplMeta.variables, values, name);
      const ok = await onSendTemplate(selTpl.name, params);
      if (ok) onClose();
    }
  }

  const mobileCls = forceMobile ? ' mobile' : '';
  const prevCls = showPrev ? ' show-prev' : '';

  return (
    <div className="ti-sp-overlay" role="dialog" aria-modal="true" aria-label="Send a message">
      <div className={`ti-sp-modal${mobileCls}${prevCls}`}>
        {/* sticky header */}
        <div className="ti-sp-head">
          <div className="ti-sp-head-top">
            <div>
              <div className="ti-sp-title">Send a message</div>
              <div className="ti-sp-sub">
                To <b>{recipientLabel}</b>
                {recipientPhone ? ` · ${recipientPhone}` : ''}
              </div>
            </div>
            <button type="button" className="ti-sp-x" aria-label="Close" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="ti-sp-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'canned'}
              className={`ti-sp-tab${tab === 'canned' ? ' on' : ''}${cannedLocked ? ' locked' : ''}`}
              onClick={() => switchTab('canned')}
              disabled={cannedLocked}
              title={cannedLocked ? 'Canned messages resume when the customer replies' : undefined}
            >
              {cannedLocked ? '🔒' : '⚡'} Canned <span className="ti-sp-cnt">{canned.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'tpl'}
              className={`ti-sp-tab${tab === 'tpl' ? ' on' : ''}`}
              onClick={() => switchTab('tpl')}
            >
              📋 Templates <span className="ti-sp-cnt">{templates.length}</span>
            </button>
          </div>
        </div>

        {/* window banner */}
        <div className={`ti-sp-winbar ${windowOpen ? 'in' : 'out'}`}>
          {windowOpen
            ? '🟢 Customer messaged within 24 h — send a canned message or a template.'
            : '🟠 Outside 24 h — approved templates only. Canned messages resume when the customer replies.'}
        </div>

        {/* body: list + preview */}
        <div className="ti-sp-body">
          <div className="ti-sp-list-pane">
            <div className="ti-sp-search">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search messages…"
                aria-label="Search messages"
                autoComplete="off"
              />
            </div>
            <div className="ti-sp-list">
              {tab === 'canned'
                ? renderCannedList(cannedGroups, search, selId, pick)
                : renderTplList(templates, search, selId, pick)}
            </div>
          </div>

          <div className="ti-sp-prev-pane">
            <button type="button" className="ti-sp-backlink" onClick={() => setShowPrev(false)}>
              ‹ Back to list
            </button>
            <div className="ti-sp-prev-scroll">
              {!selId ? (
                <div className="ti-sp-empty">
                  <div className="big">👈</div>
                  Pick a message to see exactly
                  <br />
                  what the customer receives.
                </div>
              ) : selCanned ? (
                <>
                  <div className="ti-sp-prev-h">
                    <div className="ti-sp-prev-name">{cannedTitle(selCanned)}</div>
                    {cannedHi(selCanned) ? (
                      <div className="ti-sp-langtoggle">
                        <button
                          type="button"
                          className={lang === 'en' ? 'on' : ''}
                          onClick={() => setLang('en')}
                        >
                          English
                        </button>
                        <button
                          type="button"
                          className={lang === 'hi' ? 'on' : ''}
                          onClick={() => setLang('hi')}
                        >
                          हिन्दी
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <div className="ti-sp-prev-meta">Canned message · {cannedId(selCanned)} · your words, instant</div>
                  {renderFields(
                    selCannedVars.map((tok) => ({
                      key: tok,
                      label: humanize(tok),
                      isName: isNameTok(tok),
                      value: values[tok] || '',
                    })),
                    setVal,
                    source,
                    'canned',
                  )}
                  <div className="ti-sp-section-label">Customer will see</div>
                  <Bubble segs={previewSegs} />
                </>
              ) : selTpl && selTplMeta ? (
                <>
                  <div className="ti-sp-prev-h">
                    <div className="ti-sp-prev-name">
                      {tplTitle(selTpl)} <span className="ti-sp-badge tpl">APPROVED · EN</span>
                    </div>
                    <div className="ti-sp-prev-meta">
                      <code>{selTpl.name}</code>
                    </div>
                  </div>
                  <div className="ti-sp-prev-meta">WhatsApp-approved template · sendable any time</div>
                  {renderFields(
                    selTplMeta.variables.map((lab, i) => ({
                      key: `v${i + 1}`,
                      label: lab,
                      isName: isNameLabel(lab),
                      value: values[`v${i + 1}`] || '',
                    })),
                    setVal,
                    source,
                    'tpl',
                  )}
                  <div className="ti-sp-section-label">Customer will see</div>
                  <Bubble segs={previewSegs} />
                  <div className="ti-sp-approxnote">
                    ℹ️ Preview approximate — final wording follows the exact Meta-approved template.
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* sticky footer */}
        <div className="ti-sp-foot">
          <div className="ti-sp-foot-hint">
            {tab === 'canned'
              ? '⚡ Your own words · 24-hour window only.'
              : '📋 WhatsApp-approved · any time.'}
          </div>
          <div className="ti-sp-foot-btns">
            {selId && missing > 0 ? (
              <span className="ti-sp-blockmsg">
                {missing} blank{missing > 1 ? 's' : ''} to fill
              </span>
            ) : null}
            <button type="button" className="ti-sp-btn" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button
              type="button"
              className="ti-sp-btn primary"
              onClick={() => void doSend()}
              disabled={!ready || sending}
            >
              {sending ? 'Sending…' : tab === 'canned' ? 'Send canned message' : 'Send template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── list renderers ───────────────────────── */

function renderCannedList(
  groups: { category: string; items: CannedReply[] }[],
  q: string,
  selId: string | null,
  pick: (id: string) => void,
) {
  const query = q.trim().toLowerCase();
  const out: JSX.Element[] = [];
  for (const grp of groups) {
    const items = grp.items.filter((c) => {
      if (!query) return true;
      return `${cannedTitle(c)} ${cannedEn(c)} ${cannedHi(c)} ${cannedId(c)}`
        .toLowerCase()
        .includes(query);
    });
    if (!items.length) continue;
    out.push(
      <div className="ti-sp-grp" key={`g-${grp.category}`}>
        {grp.category}
      </div>,
    );
    for (const c of items) {
      const nv = cannedVars(cannedEn(c), cannedHi(c)).filter((t) => !isNameTok(t)).length;
      const id = cannedId(c);
      out.push(
        <button
          type="button"
          key={id}
          className={`ti-sp-item${id === selId ? ' on' : ''}`}
          onClick={() => pick(id)}
        >
          <div className="ti-sp-item-t">
            {cannedTitle(c)}
            {nv ? <span className="ti-sp-badge fill">{nv} to fill</span> : null}
          </div>
          <div className="ti-sp-item-p">{previewPlain(cannedEn(c))}</div>
        </button>,
      );
    }
  }
  if (!out.length) return <div className="ti-sp-nomatch">No matches.</div>;
  return out;
}

function renderTplList(
  templates: ApprovedTemplate[],
  q: string,
  selId: string | null,
  pick: (id: string) => void,
) {
  const query = q.trim().toLowerCase();
  const items = templates.filter((t) => {
    if (!query) return true;
    const meta = metaFor(t);
    return `${t.name} ${meta.body}`.toLowerCase().includes(query);
  });
  if (!items.length) return <div className="ti-sp-nomatch">No matches.</div>;
  return items.map((t) => {
    const meta = metaFor(t);
    const nonName = meta.variables.filter((l) => !isNameLabel(l)).length;
    return (
      <button
        type="button"
        key={t.name}
        className={`ti-sp-item${t.name === selId ? ' on' : ''}`}
        onClick={() => pick(t.name)}
      >
        <div className="ti-sp-item-t">
          {tplTitle(t)}
          {nonName ? <span className="ti-sp-badge fill">{nonName} to fill</span> : null}
        </div>
        <div className="ti-sp-item-p">
          <code>{t.name}</code> {meta.body ? `— ${previewTplPlain(meta.body, meta.variables)}` : ''}
        </div>
      </button>
    );
  });
}

/* ───────────────────────── fields ───────────────────────── */

function renderFields(
  fields: { key: string; label: string; isName: boolean; value: string }[],
  setVal: (key: string, val: string) => void,
  source: ReturnType<typeof resolveName>['source'],
  kind: 'canned' | 'tpl',
) {
  if (!fields.length) {
    return (
      <>
        <div className="ti-sp-section-label">Ready</div>
        <div className="ti-sp-nofields">No blanks — ready to send.</div>
      </>
    );
  }
  return (
    <>
      <div className="ti-sp-section-label">Fill in the blanks</div>
      <div className="ti-sp-fields">
        {fields.map((f) => (
          <div className="ti-sp-field" key={f.key}>
            <label>
              {f.label}{' '}
              {f.isName ? (
                <span className="opt">(optional)</span>
              ) : (
                <span className="req">*</span>
              )}
            </label>
            <input
              value={f.value}
              data-fieldkey={f.key}
              onChange={(e) => setVal(f.key, e.target.value)}
              placeholder={f.isName ? 'leave blank → uses the greeting / “there”' : `e.g. ${f.label}`}
              aria-label={f.label}
            />
            {f.isName ? <div className="ti-sp-hint">{nameHint(source, kind)}</div> : null}
          </div>
        ))}
      </div>
    </>
  );
}

/* ───────────────────────── tiny helpers ───────────────────────── */

function tplTitle(t: ApprovedTemplate): string {
  // friendly title from the message type, else the template name
  return t.messageType ? humanize(t.messageType) : t.name;
}
/** One-line list preview of a canned EN body with ‹Label› for blanks. */
function previewPlain(body: string): string {
  return body.replace(/\{\{(\w+)\}\}/g, (_m, tok) => `‹${humanize(tok)}›`);
}
function previewTplPlain(body: string, variables: string[]): string {
  return body.replace(/\{\{(\d+)\}\}/g, (_m, n) => `‹${variables[Number(n) - 1] || n}›`);
}
